package com.ragnorak.clanybackoffice.ui.screens

import com.clany.backoffice.data.network.response.NotificationType
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Notifications
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp

// ── Main screen ───────────────────────────────────────────────────────────────

@Composable
fun NotificationsScreen(viewModel: NotificationsViewModel) {
    val state by viewModel.uiState.collectAsState()
    val scrollState = rememberScrollState()

    Box(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp)
                .verticalScroll(scrollState)
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    Icons.Default.Notifications,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    "Enviar Notificaciones",
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold
                )
            }
            Spacer(modifier = Modifier.height(24.dp))

            // Two-column layout
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.Top
            ) {
                // ── Left column: Message content ─────────────────────────────
                Card(
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(12.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                ) {
                    Column(modifier = Modifier.padding(24.dp)) {
                        Text(
                            "Contenido del mensaje",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                        Spacer(modifier = Modifier.height(16.dp))

                        // Channel toggle
                        SingleChoiceSegmentedButtonRow(modifier = Modifier.fillMaxWidth()) {
                            SegmentedButton(
                                selected = state.channel == NotificationChannel.PUSH,
                                onClick = { viewModel.updateChannel(NotificationChannel.PUSH) },
                                shape = SegmentedButtonDefaults.itemShape(index = 0, count = 2)
                            ) { Text("Notificación Push") }
                            SegmentedButton(
                                selected = state.channel == NotificationChannel.EMAIL,
                                onClick = { viewModel.updateChannel(NotificationChannel.EMAIL) },
                                shape = SegmentedButtonDefaults.itemShape(index = 1, count = 2)
                            ) { Text("Email") }
                        }
                        Spacer(modifier = Modifier.height(16.dp))

                        // Notification type selector
                        Text(
                            "Tipo de notificación",
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(modifier = Modifier.height(6.dp))
                        val notifTypes = NotificationType.entries
                        val notifTypeLabels = mapOf(
                            NotificationType.REENGAGEMENT to "Reactivación",
                            NotificationType.PROMOTIONAL to "Promocional",
                            NotificationType.ANNOUNCEMENT to "Anuncio",
                            NotificationType.REMINDER to "Recordatorio",
                            NotificationType.MAINTENANCE to "Mantenimiento"
                        )
                        SingleChoiceSegmentedButtonRow(modifier = Modifier.fillMaxWidth()) {
                            notifTypes.forEachIndexed { index, type ->
                                SegmentedButton(
                                    selected = state.notificationType == type,
                                    onClick = { viewModel.updateNotificationType(type) },
                                    shape = SegmentedButtonDefaults.itemShape(index = index, count = notifTypes.size)
                                ) {
                                    Text(notifTypeLabels[type] ?: type.name, style = MaterialTheme.typography.labelSmall)
                                }
                            }
                        }
                        Spacer(modifier = Modifier.height(16.dp))

                        OutlinedTextField(
                            value = state.title,
                            onValueChange = { viewModel.updateTitle(it) },
                            label = { Text(if (state.channel == NotificationChannel.PUSH) "Título" else "Asunto") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true
                        )
                        Spacer(modifier = Modifier.height(16.dp))

                        OutlinedTextField(
                            value = state.body,
                            onValueChange = { viewModel.updateBody(it) },
                            label = { Text(if (state.channel == NotificationChannel.PUSH) "Cuerpo" else "Cuerpo HTML") },
                            modifier = Modifier.fillMaxWidth(),
                            minLines = 4,
                            maxLines = 8
                        )

                        // Custom data (push only)
                        if (state.channel == NotificationChannel.PUSH) {
                            Spacer(modifier = Modifier.height(24.dp))
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Text(
                                    "Datos personalizados",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    modifier = Modifier.weight(1f)
                                )
                                IconButton(onClick = { viewModel.addDataField() }) {
                                    Icon(Icons.Default.Add, contentDescription = "Añadir campo")
                                }
                            }
                            if (state.dataKeys.isEmpty()) {
                                Text(
                                    "Sin campos de datos personalizados.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            } else {
                                state.dataKeys.forEachIndexed { index, key ->
                                    Row(
                                        modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp),
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                    ) {
                                        OutlinedTextField(
                                            value = key,
                                            onValueChange = { viewModel.updateDataKey(index, it) },
                                            label = { Text("Clave") },
                                            modifier = Modifier.weight(1f),
                                            singleLine = true
                                        )
                                        OutlinedTextField(
                                            value = state.dataValues[index],
                                            onValueChange = { viewModel.updateDataValue(index, it) },
                                            label = { Text("Valor") },
                                            modifier = Modifier.weight(1f),
                                            singleLine = true
                                        )
                                        IconButton(onClick = { viewModel.removeDataField(index) }) {
                                            Icon(
                                                Icons.Default.Delete,
                                                contentDescription = "Eliminar campo",
                                                tint = MaterialTheme.colorScheme.error
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // ── Right column: Filters ────────────────────────────────────
                Card(
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(12.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                ) {
                    Column(modifier = Modifier.padding(24.dp)) {
                        Text(
                            "Filtros de destinatarios",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            "Deja los campos vacíos para incluir todos los usuarios.",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(modifier = Modifier.height(16.dp))

                        // 1. Perfil de usuario
                        FilterSection(title = "Perfil de usuario") {
                            // User types
                            FilterLabel("Tipo de usuario")
                            CheckboxRow(
                                options = listOf("FREE", "PREMIUM", "TESTER"),
                                labels = mapOf("FREE" to "Gratuito", "PREMIUM" to "Premium", "TESTER" to "Tester"),
                                selected = state.selectedUserTypes,
                                onToggle = { viewModel.toggleUserType(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            // User statuses
                            FilterLabel("Estado del usuario")
                            CheckboxRow(
                                options = listOf("ACTIVE", "DELETED"),
                                labels = mapOf("ACTIVE" to "Activo", "DELETED" to "Eliminado"),
                                selected = state.selectedUserStatuses,
                                onToggle = { viewModel.toggleUserStatus(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            // Locales
                            FilterLabel("Idiomas (separados por coma, p.ej. es, en-US)")
                            OutlinedTextField(
                                value = state.localesRaw,
                                onValueChange = { viewModel.updateLocalesRaw(it) },
                                placeholder = { Text("es, en-US, fr") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            // Auth providers
                            FilterLabel("Proveedor de autenticación")
                            CheckboxRow(
                                options = listOf("EMAIL", "GOOGLE", "APPLE"),
                                labels = mapOf("EMAIL" to "Email", "GOOGLE" to "Google", "APPLE" to "Apple"),
                                selected = state.selectedAuthProviders,
                                onToggle = { viewModel.toggleAuthProvider(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Versión de la app (separadas por coma)")
                            OutlinedTextField(
                                value = state.appVersionsRaw,
                                onValueChange = { viewModel.updateAppVersionsRaw(it) },
                                placeholder = { Text("1.5.0, 1.4.2") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            // isAnonymized
                            FilterLabel("¿Usuario anonimizado?")
                            TriStateRow(
                                current = state.isAnonymized,
                                onSelect = { viewModel.setIsAnonymized(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            // shouldUpdateTyc
                            FilterLabel("¿Debe aceptar nuevos TyC?")
                            TriStateRow(
                                current = state.shouldUpdateTyc,
                                onSelect = { viewModel.setShouldUpdateTyc(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            // hasLinkedMember
                            FilterLabel("¿Tiene miembro vinculado?")
                            TriStateRow(
                                current = state.hasLinkedMember,
                                onSelect = { viewModel.setHasLinkedMember(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            // Date ranges
                            FilterLabel("Creado después de (AAAA-MM-DD)")
                            OutlinedTextField(
                                value = state.createdAfter,
                                onValueChange = { viewModel.updateCreatedAfter(it) },
                                placeholder = { Text("2024-01-01") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            FilterLabel("Creado antes de (AAAA-MM-DD)")
                            OutlinedTextField(
                                value = state.createdBefore,
                                onValueChange = { viewModel.updateCreatedBefore(it) },
                                placeholder = { Text("2025-12-31") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Último acceso después de (AAAA-MM-DD)")
                            OutlinedTextField(
                                value = state.lastActiveAfter,
                                onValueChange = { viewModel.updateLastActiveAfter(it) },
                                placeholder = { Text("2024-06-01") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            FilterLabel("Último acceso antes de (AAAA-MM-DD)")
                            OutlinedTextField(
                                value = state.lastActiveBefore,
                                onValueChange = { viewModel.updateLastActiveBefore(it) },
                                placeholder = { Text("2025-12-31") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                        }

                        Spacer(modifier = Modifier.height(8.dp))

                        // 2. Dispositivos (push only)
                        if (state.channel == NotificationChannel.PUSH) {
                            FilterSection(title = "Dispositivos") {
                                FilterLabel("Plataformas")
                                CheckboxRow(
                                    options = listOf("ANDROID", "IOS", "ALL"),
                                    labels = mapOf("ANDROID" to "Android", "IOS" to "iOS", "ALL" to "Todas"),
                                    selected = state.selectedPlatforms,
                                    onToggle = { viewModel.togglePlatform(it) }
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                                FilterLabel("¿Tiene tokens de dispositivo?")
                                TriStateRow(
                                    current = state.hasDeviceTokens,
                                    onSelect = { viewModel.setHasDeviceTokens(it) }
                                )
                            }
                            Spacer(modifier = Modifier.height(8.dp))
                        }

                        // 3. Clanes / Cuentas
                        FilterSection(title = "Clanes / Cuentas") {
                            FilterLabel("¿Tiene cuentas?")
                            TriStateRow(
                                current = state.hasAccounts,
                                onSelect = { viewModel.setHasAccounts(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. cuentas")
                                    OutlinedTextField(
                                        value = state.minAccountCount,
                                        onValueChange = { viewModel.updateMinAccountCount(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. cuentas")
                                    OutlinedTextField(
                                        value = state.maxAccountCount,
                                        onValueChange = { viewModel.updateMaxAccountCount(it) },
                                        placeholder = { Text("10") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Rol en la cuenta")
                            CheckboxRow(
                                options = listOf("OWNER", "MEMBER"),
                                labels = mapOf(
                                    "OWNER" to "Propietario",
                                    "MEMBER" to "Miembro"
                                ),
                                selected = state.selectedAccountRoles,
                                onToggle = { viewModel.toggleAccountRole(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Estado del usuario en la cuenta")
                            CheckboxRow(
                                options = listOf("ACTIVE", "REMOVED_BY_SELF"),
                                labels = mapOf("ACTIVE" to "Activo", "REMOVED_BY_SELF" to "Eliminado"),
                                selected = state.selectedAccountUserStatuses,
                                onToggle = { viewModel.toggleAccountUserStatus(it) }
                            )
                        }

                        Spacer(modifier = Modifier.height(8.dp))

                        // 4. Suscripciones
                        FilterSection(title = "Suscripciones") {
                            FilterLabel("¿Tiene suscripción activa?")
                            TriStateRow(
                                current = state.hasActiveSubscription,
                                onSelect = { viewModel.setHasActiveSubscription(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Producto (productId)")
                            CheckboxRow(
                                options = listOf("clany_premium_monthly:premium", "com.ragnorakdev.clany"),
                                labels = mapOf(
                                    "clany_premium_monthly:premium" to "Premium mensual (Play Store)",
                                    "com.ragnorakdev.clany" to "Premium (App Store)"
                                ),
                                selected = state.selectedSubscriptionPlans,
                                onToggle = { viewModel.toggleSubscriptionPlan(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Tipo de evento")
                            CheckboxRow(
                                options = listOf("INITIAL_PURCHASE", "RENEWAL", "EXPIRATION", "CANCELLATION"),
                                labels = mapOf(
                                    "INITIAL_PURCHASE" to "Primera compra",
                                    "RENEWAL" to "Renovación",
                                    "EXPIRATION" to "Expiración",
                                    "CANCELLATION" to "Cancelación"
                                ),
                                selected = state.selectedSubscriptionStatuses,
                                onToggle = { viewModel.toggleSubscriptionStatus(it) }
                            )
                        }

                        Spacer(modifier = Modifier.height(8.dp))

                        // 5. Miembros
                        FilterSection(title = "Miembros") {
                            FilterLabel("¿Ha creado miembros en sus clanes?")
                            TriStateRow(
                                current = state.isMember,
                                onSelect = { viewModel.setIsMember(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. miembros")
                                    OutlinedTextField(
                                        value = state.minMemberCount,
                                        onValueChange = { viewModel.updateMinMemberCount(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. miembros")
                                    OutlinedTextField(
                                        value = state.maxMemberCount,
                                        onValueChange = { viewModel.updateMaxMemberCount(it) },
                                        placeholder = { Text("10") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(8.dp))

                        // 6. Eventos
                        FilterSection(title = "Eventos") {
                            FilterLabel("¿Tiene eventos?")
                            TriStateRow(
                                current = state.hasEvents,
                                onSelect = { viewModel.setHasEvents(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. eventos")
                                    OutlinedTextField(
                                        value = state.minEventCount,
                                        onValueChange = { viewModel.updateMinEventCount(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. eventos")
                                    OutlinedTextField(
                                        value = state.maxEventCount,
                                        onValueChange = { viewModel.updateMaxEventCount(it) },
                                        placeholder = { Text("100") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Tipo de evento")
                            CheckboxRow(
                                options = listOf("PERSONAL", "FAMILY", "WORK", "HEALTH", "SCHOOL", "HOME", "SPECIAL", "REMINDER", "OTHER"),
                                labels = mapOf(
                                    "PERSONAL" to "Personal",
                                    "FAMILY" to "Familia",
                                    "WORK" to "Trabajo",
                                    "HEALTH" to "Salud",
                                    "SCHOOL" to "Colegio",
                                    "HOME" to "Hogar",
                                    "SPECIAL" to "Especial",
                                    "REMINDER" to "Recordatorio",
                                    "OTHER" to "Otro"
                                ),
                                selected = state.selectedEventTypes,
                                onToggle = { viewModel.toggleEventType(it) }
                            )
                        }

                        Spacer(modifier = Modifier.height(8.dp))

                        // 8. Invitaciones
                        FilterSection(title = "Invitaciones") {
                            FilterLabel("¿Ha enviado invitaciones?")
                            TriStateRow(
                                current = state.hasSentInvitations,
                                onSelect = { viewModel.setHasSentInvitations(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("¿Ha recibido invitaciones?")
                            TriStateRow(
                                current = state.hasReceivedInvitations,
                                onSelect = { viewModel.setHasReceivedInvitations(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Estado de la invitación")
                            CheckboxRow(
                                options = listOf("PENDING", "ACCEPTED", "REVOKED", "REJECTED"),
                                labels = mapOf(
                                    "PENDING" to "Pendiente",
                                    "ACCEPTED" to "Aceptada",
                                    "REVOKED" to "Revocada",
                                    "REJECTED" to "Rechazada"
                                ),
                                selected = state.selectedInvitationStatuses,
                                onToggle = { viewModel.toggleInvitationStatus(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. invitaciones enviadas")
                                    OutlinedTextField(
                                        value = state.minSentInvitations,
                                        onValueChange = { viewModel.updateMinSentInvitations(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. invitaciones enviadas")
                                    OutlinedTextField(
                                        value = state.maxSentInvitations,
                                        onValueChange = { viewModel.updateMaxSentInvitations(it) },
                                        placeholder = { Text("100") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(8.dp))
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. invitaciones recibidas")
                                    OutlinedTextField(
                                        value = state.minReceivedInvitations,
                                        onValueChange = { viewModel.updateMinReceivedInvitations(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. invitaciones recibidas")
                                    OutlinedTextField(
                                        value = state.maxReceivedInvitations,
                                        onValueChange = { viewModel.updateMaxReceivedInvitations(it) },
                                        placeholder = { Text("10") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(8.dp))

                        // 9. IDs específicos
                        FilterSection(title = "IDs específicos") {
                            FilterLabel("Incluir solo estos usuarios (uno por línea)")
                            OutlinedTextField(
                                value = state.includeUserIdsRaw,
                                onValueChange = { viewModel.updateIncludeUserIdsRaw(it) },
                                placeholder = { Text("user_abc123\nuser_def456") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 3,
                                maxLines = 6
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            FilterLabel("Excluir estos usuarios (uno por línea)")
                            OutlinedTextField(
                                value = state.excludeUserIdsRaw,
                                onValueChange = { viewModel.updateExcludeUserIdsRaw(it) },
                                placeholder = { Text("user_xyz789") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 3,
                                maxLines = 6
                            )
                        }

                        Spacer(modifier = Modifier.height(16.dp))
                        HorizontalDivider()
                        Spacer(modifier = Modifier.height(16.dp))

                        // Preview result
                        if (state.previewCount != null) {
                            Card(
                                modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                                colors = CardDefaults.cardColors(
                                    containerColor = MaterialTheme.colorScheme.primaryContainer
                                )
                            ) {
                                Column(modifier = Modifier.padding(16.dp)) {
                                    Text(
                                        "Destinatarios estimados: ${state.previewCount}",
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.Bold,
                                        color = MaterialTheme.colorScheme.onPrimaryContainer
                                    )
                                    if (state.previewUsers.isNotEmpty()) {
                                        Spacer(modifier = Modifier.height(4.dp))
                                        Text(
                                            "Ejemplo: " + state.previewUsers.joinToString(", "),
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onPrimaryContainer
                                        )
                                    }
                                }
                            }
                        }

                        val previewError = state.previewError
                        if (previewError != null) {
                            Text(
                                previewError,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodySmall,
                                modifier = Modifier.padding(bottom = 12.dp)
                            )
                        }

                        // Preview button
                        Button(
                            onClick = { viewModel.calculatePreview() },
                            modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.secondary
                            ),
                            enabled = !state.isPreviewing
                        ) {
                            if (state.isPreviewing) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(20.dp),
                                    color = MaterialTheme.colorScheme.onSecondary,
                                    strokeWidth = 2.dp
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text("Calculando...")
                            } else {
                                Text("Calcular destinatarios")
                            }
                        }

                        // Send button
                        Button(
                            onClick = { viewModel.sendNotification() },
                            modifier = Modifier.fillMaxWidth().height(48.dp),
                            enabled = !state.isSending &&
                                state.previewCount != null &&
                                state.title.isNotBlank() &&
                                state.body.isNotBlank()
                        ) {
                            if (state.isSending) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(24.dp),
                                    color = MaterialTheme.colorScheme.onPrimary,
                                    strokeWidth = 2.dp
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text("Enviando...")
                            } else {
                                Icon(
                                    if (state.channel == NotificationChannel.PUSH) Icons.Default.Notifications
                                    else Icons.Default.Email,
                                    contentDescription = null,
                                    modifier = Modifier.size(18.dp)
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(
                                    if (state.previewCount == null) "Calcula los destinatarios primero"
                                    else if (state.channel == NotificationChannel.PUSH) "Enviar notificación push"
                                    else "Enviar email"
                                )
                            }
                        }
                    }
                }
            }
        }

        // ── Feedback overlay ──────────────────────────────────────────────────
        if (state.successMessage != null || state.errorMessage != null) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.5f)),
                contentAlignment = Alignment.Center
            ) {
                Card(
                    modifier = Modifier.width(420.dp),
                    shape = RoundedCornerShape(16.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            if (state.successMessage != null) "Éxito" else "Error",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold,
                            color = if (state.successMessage != null)
                                MaterialTheme.colorScheme.primary
                            else
                                MaterialTheme.colorScheme.error
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        Text(
                            state.successMessage ?: state.errorMessage ?: "",
                            style = MaterialTheme.typography.bodyMedium
                        )
                        Spacer(modifier = Modifier.height(24.dp))
                        Button(onClick = { viewModel.dismissMessage() }) {
                            Text("Cerrar")
                        }
                    }
                }
            }
        }
    }
}

// Filter composables (FilterLabel, CheckboxRow, TriStateRow, FilterSection) are in FilterComponents.kt
